import React, { useEffect, useMemo, useRef, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api, API_BASE } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Upload, Download, Timer } from "lucide-react";
import ImageUpload from "@/components/ImageUpload";
import { localizeError } from "@/lib/errors";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";

const empty = { name_en: "", name_ar: "", sku: "", description_en: "", description_ar: "", category: "general", category_id: "", price: 0, duration_min: 30, active: true, show_on_website: true, is_featured: false, image_url: "" };
const PAGE_SIZE = 50;
const customCategoryValue = (text) => `__custom__:${text}`;
const parseCategorySelectValue = (value) => {
  if (!value) return { category_id: "", category: "general" };
  if (value.startsWith("__custom__:")) {
    return { category_id: "", category: value.slice("__custom__:".length) };
  }
  return { category_id: value, category: "" };
};

export default function Services() {
  const { t, lang } = useLanguage();
  const { confirm, ConfirmDialogSlot } = useConfirmDialog();
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const fileRef = useRef(null);

  // Excel template download — backend builds a styled workbook with sample row.
  const downloadTemplate = async () => {
    try {
      const r = await fetch(`${API_BASE}/services/template`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = "services_template.xlsx";
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error(lang === "ar" ? "تعذر تنزيل القالب" : "Failed to download template"); }
  };

  const exportExcel = async () => {
    try {
      const r = await fetch(`${API_BASE}/services/export`, { credentials: "include" });
      if (!r.ok) throw new Error();
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `services_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success(lang === "ar" ? `تم تصدير ${items.length} خدمة` : `Exported ${items.length} services`);
    } catch { toast.error(lang === "ar" ? "فشل التصدير" : "Export failed"); }
  };

  const onFile = async (ev) => {
    const f = ev.target.files?.[0];
    if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const r = await api.post("/services/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { created = 0, skipped = 0, errors = [] } = r.data || {};
      if (created > 0) {
        toast.success(`${created} ${t("imported_count")}${skipped ? `, ${skipped} ${t("skipped_count")}` : ""}`);
      } else if (skipped > 0 || errors.length) {
        const detail = errors.slice(0, 2).join(" · ");
        toast.error(
          lang === "ar"
            ? `لم يتم استيراد أي خدمة. ${skipped ? `${skipped} صفوف تم تخطيها. ` : ""}${detail}`
            : `No services imported.${skipped ? ` ${skipped} row(s) skipped.` : ""} ${detail}`,
        );
      } else {
        toast.warning(lang === "ar" ? "لم يتم العثور على صفوف بيانات" : "No data rows found in file");
      }
      ev.target.value = "";
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "ar" ? "فشل الاستيراد" : "Import failed"));
    }
  };

  const repairDurations = async () => {
    try {
      const preview = await api.post("/services/repair-duration-encoding?dry_run=true");
      const count = preview.data?.would_update ?? 0;
      if (!count) {
        toast.info(lang === "ar" ? "لا توجد مدد تحتاج تصحيح" : "No encoded durations to fix");
        return;
      }
      const sample = (preview.data?.changes || []).slice(0, 3)
        .map((c) => `${c.name_en}: ${c.from_minutes}→${c.to_minutes}`)
        .join(", ");
      const ok = await confirm({
        title: lang === "ar" ? `تصحيح ${count} خدمة؟` : `Fix ${count} service duration(s)?`,
        description: lang === "ar"
          ? `مثال: ${sample}\n\n1 = ساعة واحدة، 430 = 4س 30د`
          : `Example: ${sample}\n\n1 = 1 hour, 430 = 4h 30m`,
        confirmLabel: lang === "ar" ? "تأكيد" : "Confirm",
        cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
        variant: "primary",
      });
      if (!ok) return;
      const r = await api.post("/services/repair-duration-encoding?dry_run=false");
      toast.success(lang === "ar" ? `تم تصحيح ${r.data?.updated ?? 0} خدمة` : `Fixed ${r.data?.updated ?? 0} service(s)`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || (lang === "ar" ? "فشل التصحيح" : "Repair failed"));
    }
  };

  const load = async () => {
    // Wave B — services dropdown only sees service-typed (or "both") categories
    const [r, c] = await Promise.all([
      api.get("/services"),
      api.get("/categories?type=service"),
    ]);
    setItems(r.data);
    setCategories(c.data);
  };
  useEffect(() => { load(); }, []);
  useEffect(() => { setPage(1); }, [search]);

  const categoryLabel = (s) => {
    if (s.category_id) {
      const cat = categories.find((c) => c.category_id === s.category_id);
      if (cat) return lang === "ar" ? cat.name_ar : cat.name_en;
    }
    return s.category || "—";
  };

  const categorySelectOptions = useMemo(() => {
    const known = new Set();
    categories.forEach((c) => {
      if (c.name_en) known.add(c.name_en.trim().toLowerCase());
      if (c.name_ar) known.add(c.name_ar.trim().toLowerCase());
    });
    const imported = new Map();
    items.forEach((s) => {
      const text = (s.category || "").trim();
      if (!text || text.toLowerCase() === "general") return;
      if (s.category_id && categories.some((c) => c.category_id === s.category_id)) return;
      const key = text.toLowerCase();
      if (!known.has(key)) imported.set(key, text);
    });
    return [...imported.values()].sort((a, b) => a.localeCompare(b, lang === "ar" ? "ar" : "en"));
  }, [items, categories, lang]);

  const categorySelectValue = form.category_id
    || (form.category && form.category !== "general" ? customCategoryValue(form.category) : "");

  const filteredItems = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return items;
    return items.filter((s) => {
      const cat = categoryLabel(s).toLowerCase();
      return (
        (s.name_en || "").toLowerCase().includes(q) ||
        (s.name_ar || "").toLowerCase().includes(q) ||
        (s.sku || "").toLowerCase().includes(q) ||
        cat.includes(q)
      );
    });
  }, [items, search, categories, lang]);

  const totalPages = Math.max(1, Math.ceil(filteredItems.length / PAGE_SIZE));
  const pageItems = useMemo(
    () => filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [filteredItems, page],
  );

  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);

  const submit = async () => {
    try {
      const cat = categories.find((c) => c.category_id === form.category_id);
      const payload = {
        ...form,
        sku: (form.sku || "").trim(),
        category: cat
          ? (lang === "ar" ? cat.name_ar : cat.name_en)
          : (form.category || "general"),
        category_id: form.category_id || null,
        price: parseFloat(form.price) || 0,
        duration_min: parseInt(form.duration_min, 10) || 30,
      };
      if (editId) await api.put(`/services/${editId}`, payload);
      else await api.post("/services", payload);
      toast.success(t("saved")); setOpen(false); setForm(empty); setEditId(null); load();
    } catch (e) { toast.error(localizeError(e, t, lang)); }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: "Delete?",
      confirmLabel: lang === "ar" ? "حذف" : "Delete",
      cancelLabel: lang === "ar" ? "إلغاء" : "Cancel",
      variant: "danger",
    });
    if (!ok) return;
    await api.delete(`/services/${id}`); load();
  };

  const openEdit = (s) => {
    let category_id = s.category_id || "";
    let category = s.category || "general";
    if (!category_id && category && category !== "general") {
      const match = categories.find((c) =>
        (c.name_en || "").trim().toLowerCase() === category.trim().toLowerCase()
        || (c.name_ar || "").trim().toLowerCase() === category.trim().toLowerCase(),
      );
      if (match) category_id = match.category_id;
    }
    setForm({
      ...empty,
      ...s,
      sku: s.sku || "",
      category_id,
      category,
    });
    setEditId(s.service_id);
    setOpen(true);
  };

  return (
    <div className="space-y-6" data-testid="services-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="font-heading text-4xl gold-text">{t("nav_services")}</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={downloadTemplate} data-testid="services-template-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Download size={14}/>{t("download_template")}
          </button>
          <input ref={fileRef} type="file" accept=".xlsx,.xls" onChange={onFile} className="hidden" data-testid="services-import-file"/>
          <button onClick={() => fileRef.current?.click()} data-testid="services-import-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Upload size={14}/>{t("import_excel")}
          </button>
          <button onClick={exportExcel} data-testid="services-export-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2">
            <Download size={14}/>{lang === "ar" ? "تصدير" : "Export"}
          </button>
          <button onClick={repairDurations} data-testid="services-repair-duration-btn" className="btn-rose-outline px-3 py-2 rounded-md text-sm flex items-center gap-2" title={lang === "ar" ? "تصحيح 1=ساعة، 430=4س30د" : "Fix 1=hour, 430=4h30m"}>
            <Timer size={14}/>{lang === "ar" ? "تصحيح المدة" : "Fix durations"}
          </button>
          <button onClick={() => { setForm(empty); setEditId(null); setOpen(true); }} data-testid="add-service-btn" className="btn-gold px-4 py-2 rounded-md flex items-center gap-2">
            <Plus size={16}/>{t("add")}
          </button>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <input
          className="input-dark max-w-sm"
          placeholder={lang === "ar" ? "ابحث بالاسم أو الفئة أو رمز المنتج..." : "Search by name, category or SKU..."}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          data-testid="services-search"
        />
        {filteredItems.length > 0 && (
          <div className="flex flex-wrap items-center gap-2">
            <div className="text-sm text-[var(--text-muted)]">
              {lang === "ar"
                ? `عرض ${Math.min((page - 1) * PAGE_SIZE + 1, filteredItems.length)}-${Math.min(page * PAGE_SIZE, filteredItems.length)} من ${filteredItems.length}`
                : `Showing ${Math.min((page - 1) * PAGE_SIZE + 1, filteredItems.length)}-${Math.min(page * PAGE_SIZE, filteredItems.length)} of ${filteredItems.length}`}
            </div>
            <button type="button" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1} className="btn-rose-outline px-3 py-2 rounded-md text-sm disabled:opacity-50">
              {lang === "ar" ? "السابق" : "Prev"}
            </button>
            <span className="text-sm text-[var(--text-muted)]">{page}/{totalPages}</span>
            <button type="button" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages} className="btn-rose-outline px-3 py-2 rounded-md text-sm disabled:opacity-50">
              {lang === "ar" ? "التالي" : "Next"}
            </button>
          </div>
        )}
      </div>

      <div className="card-surface overflow-auto">
        <table className="table-dark">
          <thead>
            <tr>
              <th>{t("name")}</th><th>{t("sku")}</th><th>{t("category")}</th><th>{t("price")}</th><th>{t("duration")}</th><th>{lang === "ar" ? "على الموقع" : "Website"}</th><th>{t("status")}</th><th>{t("actions")}</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.map(s => (
              <tr key={s.service_id}>
                <td className="text-[var(--text)]">
                  <div>{lang === "ar" ? s.name_ar : s.name_en}</div>
                  {lang === "ar" ? s.name_en && <div className="text-xs text-[var(--text-muted)]">{s.name_en}</div> : s.name_ar && <div className="text-xs text-[var(--text-muted)]">{s.name_ar}</div>}
                </td>
                <td className="text-[var(--text-muted)] font-mono text-xs">{s.sku || "—"}</td>
                <td className="text-[var(--text-muted)]">{categoryLabel(s)}</td>
                <td className="gold-text font-medium">{s.price}</td>
                <td className="text-[var(--text)]">{s.duration_min}</td>
                <td><span className={`badge ${s.show_on_website !== false ? "badge-green" : ""}`}>{s.show_on_website !== false ? "✓" : "✗"}</span></td>
                <td><span className={`badge ${s.active ? "badge-green" : ""}`}>{s.active ? t("active") : t("inactive")}</span></td>
                <td>
                  <div className="flex gap-2">
                    <button onClick={() => openEdit(s)} data-testid={`edit-service-${s.service_id}`} className="p-1.5 rounded border border-[var(--border)] hover:border-[var(--gold)]"><Pencil size={14}/></button>
                    <button onClick={() => remove(s.service_id)} data-testid={`delete-service-${s.service_id}`} className="p-1.5 rounded border border-[var(--border)] hover:border-red-500"><Trash2 size={14}/></button>
                  </div>
                </td>
              </tr>
            ))}
            {pageItems.length === 0 && <tr><td colSpan={8} className="text-center text-[var(--text-muted)] py-8">{search ? (lang === "ar" ? "لا نتائج تطابق البحث" : "No results match your search") : t("no_data")}</td></tr>}
          </tbody>
        </table>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/30 backdrop-blur-sm flex items-center justify-center p-3 sm:p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setOpen(false))}>
          <div className="card-surface p-4 sm:p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto space-y-4" onMouseDown={onModalPanelMouseDown}>
            <h2 className="font-heading text-2xl gold-text">{editId ? t("edit") : t("add")}</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div><label className="text-xs text-[var(--text-muted)]">{t("name_en")}</label><input className="input-dark" data-testid="service-name-en" value={form.name_en} onChange={e => setForm({...form, name_en: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("name_ar")}</label><input className="input-dark" data-testid="service-name-ar" value={form.name_ar} onChange={e => setForm({...form, name_ar: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("sku")}</label><input className="input-dark font-mono" data-testid="service-sku" value={form.sku || ""} onChange={e => setForm({...form, sku: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("category")}</label>
                <select
                  className="input-dark"
                  value={categorySelectValue}
                  onChange={(e) => {
                    const picked = parseCategorySelectValue(e.target.value);
                    if (picked.category_id) {
                      const cat = categories.find((c) => c.category_id === picked.category_id);
                      setForm({
                        ...form,
                        category_id: picked.category_id,
                        category: cat ? (lang === "ar" ? cat.name_ar : cat.name_en) : form.category,
                      });
                    } else {
                      setForm({
                        ...form,
                        category_id: "",
                        category: picked.category || "general",
                      });
                    }
                  }}
                  data-testid="service-category-select"
                >
                  <option value="">{lang === "ar" ? "— عام —" : "— general —"}</option>
                  {categories.map(c => (
                    <option key={c.category_id} value={c.category_id}>{lang === "ar" ? c.name_ar : c.name_en}</option>
                  ))}
                  {categorySelectOptions.length > 0 && (
                    <optgroup label={lang === "ar" ? "فئات من الاستيراد" : "Imported categories"}>
                      {categorySelectOptions.map((cat) => (
                        <option key={`imported-${cat}`} value={customCategoryValue(cat)}>{cat}</option>
                      ))}
                    </optgroup>
                  )}
                </select>
              </div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("price")}</label><input className="input-dark" type="number" data-testid="service-price" value={form.price} onChange={e => setForm({...form, price: e.target.value})}/></div>
              <div><label className="text-xs text-[var(--text-muted)]">{t("duration")}</label><input className="input-dark" type="number" value={form.duration_min} onChange={e => setForm({...form, duration_min: e.target.value})}/></div>
              <div className="flex items-center gap-2 pt-5"><input type="checkbox" checked={form.active} onChange={e => setForm({...form, active: e.target.checked})}/><span className="text-sm text-[var(--text)]">{t("active")}</span></div>
              <div className="flex items-center gap-2 pt-5"><input type="checkbox" data-testid="service-show-on-website" checked={form.show_on_website !== false} onChange={e => setForm({...form, show_on_website: e.target.checked})}/><span className="text-sm text-[var(--text)]">{lang === "ar" ? "إظهار على الموقع" : "Show on Website"}</span></div>
              <div className="flex items-center gap-2 pt-5"><input type="checkbox" data-testid="service-is-featured" checked={!!form.is_featured} onChange={e => setForm({...form, is_featured: e.target.checked})}/><span className="text-sm text-[var(--text)]">{lang === "ar" ? "خدمة مميزة (تظهر في الصفحة الرئيسية)" : "Featured (shown on homepage)"}</span></div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)] mb-1 block">{lang === "ar" ? "صورة الخدمة" : "Service Image"}</label><ImageUpload value={form.image_url || ""} onChange={(url) => setForm({...form, image_url: url})} recommendedSize="800×600" category="services" aspect="aspect-video" testIdPrefix="service-image" lang={lang} /></div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("description_en")}</label><textarea className="input-dark" rows={2} value={form.description_en} onChange={e => setForm({...form, description_en: e.target.value})}/></div>
              <div className="md:col-span-2"><label className="text-xs text-[var(--text-muted)]">{t("description_ar")}</label><textarea className="input-dark" rows={2} value={form.description_ar} onChange={e => setForm({...form, description_ar: e.target.value})}/></div>
            </div>
            <div className="flex gap-3 justify-end">
              <button className="px-4 py-2 rounded-md border border-[var(--border)] text-[var(--text)]" onClick={() => setOpen(false)}>{t("cancel")}</button>
              <button className="btn-gold px-6 py-2 rounded-md" data-testid="service-save-btn" onClick={submit}>{t("save")}</button>
            </div>
          </div>
        </div>
      )}
      <ConfirmDialogSlot />
    </div>
  );
}
